import {
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  ScrollArea,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useListState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconDeviceFloppy, IconPrinter } from "@tabler/icons-react";
import { logError } from "api/diagnostic.service";

import { formatDate, formatNZD, getLocalDateString } from "common/utils";
import React, {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInvoiceStore } from "stores/useInvoiceStore";

import { safeApiCall } from "api/common.service";
import {
  deleteJournalDetails,
  getJournalById,
  submitJournal,
} from "api/creditjnl.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_DATA_LOAD_TITLE,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
  NOTIFICATION_TITLE_JOURNALS,
} from "common/constants";
import { navigateTo } from "common/navigation";
import DefaultButtonAdd from "pages/admin/components/DefaultButtonAdd";
import { FullScreenModal, SavingModal } from "pages/modals";
import { IDataEntryFormProps } from "types/interface.types";
import {
  JournalDataEntryForm,
  JournalEntries,
  JournalEntry,
} from "types/model.types";
import AdminCreditJournalPreview from "./AdminCreditJournalPreview";
import { JournalEntryRow } from "./AdminJournalEntryRow";
import { JournalEntryHeaderRow } from "./DataEntryHeaderDefinitions";

const AdminCreditJournalEntryPage: React.FC<IDataEntryFormProps> = ({
  recordId,
  onSaveComplete,
}) => {
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = React.useState(false);
  const [headerVersion, setHeaderVersion] = useState(0);
  const [initialData, setInitialData] = useState<JournalDataEntryForm | null>(
    null,
  );
  const [invModalOpen, setInvModalOpen] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const { invoices: knownInvoices, loading: invoiceLoading } =
    useInvoiceStore();
  // Define our useForm and must be declared before any UseEffects
  const form = useForm<JournalDataEntryForm>({
    initialValues: {
      id: 0,
      summary: {
        id: 0,
        createdOn: getLocalDateString(),
        expected: 0,
        entered: 0,
        notes: "",
        isPrinted: false,
        isProcessed: false,
      },
      entries: [],
    },
  });
  const { summary } = form.getValues();
  const [entries, handlers] = useListState<JournalEntry>([]);
  const numberInputRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleConfirmSelection(); // confirm selection 1st
        setInvModalOpen(false); // then close
      }
    };

    if (invModalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [invModalOpen, selectedInvoiceIds]);

  // this removes state updates from layout-sensitve effects and removes layout feedback loop.
  // otherwise, we'll randomly get ResizeObserver loop error
  const { entered } = useMemo(() => {
    const total = entries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
    const rounded = Math.round(total * 100) / 100;
    return { entered: rounded };
  }, [entries]);

  useEffect(() => {
    form.setFieldValue("summary.entered", entered);
  }, [entered]);

  // capture insert key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Insert") {
        e.preventDefault();
        handleAddEntry();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!recordId || (recordId ?? 0) === 0) return;
    let mounted = true;

    const fetchData = async () => {
      try {
        const response = await safeApiCall(getJournalById(recordId));
        if (!mounted) return;
        setInitialData(response);
      } catch (error) {
        logError(error);
        notifications.show({
          title: MSG_DEFAULT_FORM_DATA_LOAD_TITLE,
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          color: MantineNotificationColor.Error,
        });
      }
    };
    fetchData();

    // what this protects
    // Can’t perform a React state update on an unmounted component.
    // if component is unmounted and we're setting state becuase of
    // api calls that's async etc...
    return () => {
      mounted = false;
    };
  }, [recordId]);

  useEffect(() => {
    if (!initialData) return;
    form.setValues({
      ...initialData,
      entries: [],
    });

    handlers.setState(initialData.entries ?? []);
    setSelectedInvoiceIds(initialData.entries.map((e) => String(e.invoiceId)));
  }, [initialData]);

  const handleAddEntry = () => {
    handlers.append({
      transientId: crypto.randomUUID(),
      id: 0,
      invoiceId: 0,
      createdOn: getLocalDateString(),
      debtorId: 0,
      debtorName: "",
      amount: 0,
      coOpFees: null,
      originalAmount: 0,
      effect: 0,
    });
    const newIndex = form.values.entries.length - 1;
    requestAnimationFrame(() => {
      const wrapper = numberInputRefs.current[newIndex];
      const input = wrapper?.querySelector("input");
      input?.focus();
    });
  };

  const handlePrint = async () => {
    form.setFieldValue("summary.isPrinted", true);
    handleSubmit(false, false, true);
    const { summary } = form.getValues();

    setTimeout(() => {
      navigateTo(`/print/crjnl?id=${summary.id}&e=1`);
    }, 500);

    //setPreview(true);
  };

  const knownInvoiceIds = new Set(
    knownInvoices
      .map((inv) => inv?.invoiceId)
      .filter((id): id is number => typeof id === "number" && !isNaN(id)),
  );

  const seenInvoiceIds = new Set<number>();
  const invalidEntries = form.values.entries.filter((entry) => {
    const invoiceId = Number(entry.invoiceId);
    const isKnown = knownInvoiceIds.has(invoiceId);
    const isDuplicate = seenInvoiceIds.has(invoiceId);

    seenInvoiceIds.add(invoiceId);
    return !isKnown || isDuplicate;
  });

  const canProceed = (): boolean => {
    const { summary } = form.getValues();
    return summary.expected === summary.entered && invalidEntries.length === 0;
  };
  /**
   * Process the credit note.  This works by setting the isProcessed flag to true
   * and then calling handleSubmit to save the form.  The backend will handle the rest.
   * @returns
   */
  const handleProcess = async () => {
    setTimeout(() => {
      handleSubmit(true, true);
    }, 0);
  };

  const handleSubmit = async (
    close = false,
    process = false,
    suppressNotification = false,
  ) => {
    const formData = form.getValues();
    if (formData.summary.isProcessed) return; // already processed...

    if (canProceed() === false) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message:
          "Please check expected/entered match and/or invoice IDs are valid in each line item(s) and there are no duplicate invoices",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    } // should never happen as the buttons call the canProceed and disables control if form is invalid.

    setSaving(true);
    try {
      const payload: JournalDataEntryForm = {
        ...formData,
        summary: {
          ...formData.summary,
          isProcessed: process,
        },
        entries,
      };

      const result: any = await safeApiCall(submitJournal(payload));
      // Merge backend response into form
      if (!result?.entries || !Array.isArray(result.entries)) {
        throw new Error(
          "Invalid response: entries is missing or not an array.",
        );
      }

      const updatedEntries: JournalEntries = result.entries.map(
        (updatedEntry: JournalEntry) => {
          const existing = entries?.find(
            (e) => e.invoiceId === updatedEntry.invoiceId,
          );

          return {
            ...existing,
            ...updatedEntry, // this includes the new `id`
          };
        },
      );

      startTransition(() => {
        form.setValues({
          summary: {
            ...form.values.summary,
            id: result.summary.id,
          },
          entries: [],
        });
        handlers.setState(updatedEntries);
      });

      if (!suppressNotification) {
        notifications.show({
          title: NOTIFICATION_TITLE_JOURNALS,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Success,
        });
      }

      (process || close) && onSaveComplete(close);
    } catch (error) {
      logError(error);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEntry = async (index: number) => {
    if (summary.isProcessed) return; // should never happen as the remove buttons are disabled when processed, but just in case...

    const entry = entries[index];
    const entryId = Number(entry?.id); // coerce to number safely
    const invoiceId = String(entry.invoiceId); // ensure string for comparison

    const removeFromSelected = () => {
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== invoiceId));
    };

    if (!isNaN(entryId) && entryId > 0) {
      handleRemoveEntries([entryId]);
    }
    removeFromSelected();
    handlers.remove(index); // newly added entry, just remove locally
  };

  const handleRemoveEntries = async (ids: number[]) => {
    if (ids.length === 0) return;
    const { summary } = form.getValues();
    if (summary.isProcessed) return; // should never happen as the remove buttons are disabled when processed, but just in case...

    setSaving(true);

    try {
      const response = await safeApiCall(
        deleteJournalDetails(Number(summary.id), ids),
      );
      if (response?.status === 200 && response.data?.success) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    } catch (error) {
      logError(error);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSelection = () => {
    const selectedIds = new Set(selectedInvoiceIds.map(Number));
    const existingMap = new Map(entries.map((e) => [e.invoiceId, e]));

    const combined: JournalEntries = [];
    for (const i of knownInvoices) {
      if (!selectedIds.has(i.invoiceId)) continue;

      const existing = existingMap.get(i.invoiceId);
      if (existing) {
        combined.push(existing); // preserve object identity
      } else {
        combined.push({
          transientId: crypto.randomUUID(),
          id: 0,
          invoiceId: i.invoiceId,
          debtorName: i.debtorName,
          amount: i.amount,
          coOpFees: null,
          createdOn: i.createdOn,
          debtorId: i.debtorId,
          originalAmount: i.originalAmount ?? i.amount,
          effect: i.effect ?? 0,
        });
      }
    }
    const removedIds = entries
      .filter(
        (entry) =>
          !selectedIds.has(entry.invoiceId) && Number(entry.id ?? 0) > 0,
      )
      .map((entry) => Number(entry.id));

    startTransition(() => {
      handlers.setState(combined);
      handleRemoveEntries(removedIds);
      setInvModalOpen(false);
    });
  };

  const handleInvoiceChange = (index: number, updated: JournalEntry) => {
    if (summary.isProcessed) return; // should never happen as the inputs are disabled when processed, but just in case...

    const match = knownInvoices.find(
      (inv) => inv.invoiceId === updated.invoiceId,
    );
    if (!match) {
      handlers.setItem(index, {
        ...entries[index],
        debtorName: "No invoice found",
      });
      return;
    }

    handlers.setItem(index, {
      ...entries[index],
      invoiceId: match.invoiceId,
      debtorId: match.debtorId,
      debtorName: match.debtorName,
      amount: updated.amount ?? 0,
      createdOn: match.createdOn,
    });

    // Add to selectedInvoiceIds if not already present
    setSelectedInvoiceIds((prev) => {
      const idStr = String(updated.invoiceId);
      return prev.includes(idStr) ? prev : [...prev, idStr];
    });
  };

  const [filter, setFilter] = useState("");

  const filteredInvoices = useMemo(() => {
    const f = filter.toLowerCase().trim();
    if (!f) return knownInvoices;

    return knownInvoices.filter((inv) => {
      const name = inv.debtorName?.toLowerCase() ?? "";
      const parentName = inv.parentName?.toLowerCase() ?? "";
      const id = String(inv.invoiceId);
      const parentId = String(inv.parentId ?? "");

      return (
        name.includes(f) ||
        id.includes(f) ||
        parentId.includes(f) ||
        parentName.includes(f)
      );
    });
  }, [filter, knownInvoices]);

  if (invoiceLoading) {
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack>
      <Title order={3}>Credit Journal Entry</Title>
      <Group grow align="flex-start">
        <TextInput
          label="Journal ID"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={form.values.summary.id === 0 ? "New" : form.values.summary.id}
          readOnly
        />
        <TextInput
          label="Created On"
          value={formatDate(form.values.summary.createdOn)}
          readOnly
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="Expected Amount"
          readOnly={summary.isProcessed} // only allow editing expected amount until processed. after that, it's a record of what was expected at time of processing.
          {...form.getInputProps("summary.expected")}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
            input: {
              backgroundColor:
                form.values.summary.expected !== form.values.summary.entered
                  ? "#fff9db" // light yellow
                  : undefined,
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
          }}
        />
        <NumberInput
          label="Entered Amount"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={form.values.summary.entered}
          readOnly
        />
      </Group>
      <Textarea
        label="Notes"
        disabled={summary.isProcessed} // only allow editing expected amount until processed. after that, it's a record of what was expected at time of processing.
        styles={{
          label: {
            fontWeight: 600, // makes label bold
          },
        }}
        {...form.getInputProps("summary.notes")}
        mt="md"
      />

      <Divider
        my="md"
        label={`By default, invoice search only spans last 6 months. This will not work
        if the invoice is older than 6 months. ${knownInvoices.length} invoices were loaded.`}
      />

      <Title order={3}>Invoice Entries</Title>
      <Group justify="flex-start" mt="md">
        <DefaultButtonAdd
          disabled={summary.isProcessed}
          onClick={handleAddEntry}
          text="Add Invoice"
          variant="light"
        />
        <DefaultButtonAdd
          disabled={summary.isProcessed}
          onClick={() => setInvModalOpen(true)}
          text=" Select Invoice(s)"
          variant="light"
        />
      </Group>

      <Stack gap="sm" className="form">
        <JournalEntryHeaderRow key={headerVersion} />
        {entries.map((entry, index) => (
          <JournalEntryRow
            key={entry.id || `temp-${index}`}
            entry={entry}
            index={index}
            onUpdate={(updated) => handleInvoiceChange(index, updated)}
            onRemove={() => handleRemoveEntry(index)}
          ></JournalEntryRow>
        ))}
      </Stack>

      <Group justify="space-between" mt="md">
        <DefaultButtonAdd
          disabled={summary.isProcessed}
          onClick={handleAddEntry}
          text="Add Invoice"
          variant="light"
        />

        <Group>
          <Button
            onClick={() => handleSubmit()}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || summary.isProcessed}
          >
            Save
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || summary.isProcessed}
          >
            Save & Close
          </Button>
          <Button
            onClick={handlePrint}
            disabled={
              saving || summary.id === 0 || summary.entered !== summary.expected
            }
            leftSection={<IconPrinter size={16} />}
          >
            Print
          </Button>
          <Button
            title="Process the journal. This action cannot be undone."
            onClick={handleProcess}
            disabled={
              saving ||
              summary.isProcessed ||
              summary.id === 0 ||
              summary.entered !== summary.expected
            }
            leftSection={<IconCheck size={16} />}
          >
            Process
          </Button>
        </Group>
      </Group>

      {/* Invoice selection */}
      <Modal
        size={"xl"}
        opened={invModalOpen}
        onClose={() => {
          setInvModalOpen(false);
          handleConfirmSelection();
        }}
        zIndex={3000}
        overlayProps={{ opacity: 0.3, blur: 2 }}
        title="Select Invoice(s) to Credit Against"
      >
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <ScrollArea>
            {/* 🔍 Filter box */}
            <TextInput
              placeholder="Filter by debtor name or invoice #"
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
              mb="md"
            />
            {/* Master checkbox */}
            <Checkbox
              mb="sm"
              label="Select / Unselect All"
              checked={
                filteredInvoices.length > 0 &&
                selectedInvoiceIds.length === filteredInvoices.length
              }
              indeterminate={
                selectedInvoiceIds.length > 0 &&
                selectedInvoiceIds.length < filteredInvoices.length
              }
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  setSelectedInvoiceIds(
                    filteredInvoices.map((i) => String(i.invoiceId)),
                  );
                } else {
                  setSelectedInvoiceIds([]);
                }
              }}
            />
            <Checkbox.Group
              value={selectedInvoiceIds}
              onChange={setSelectedInvoiceIds}
            >
              <Stack gap="sm">
                {filteredInvoices.map((invoice, index) => (
                  <Checkbox
                    key={`${index}-${invoice.invoiceId}`}
                    value={String(invoice.invoiceId)}
                    label={`Debtor: ${invoice.debtorName}, INV #: ${invoice.invoiceId} on ${formatDate(invoice.createdOn)}, Amount: ${formatNZD(invoice.amount ?? 0)}`}
                  />
                ))}
              </Stack>
            </Checkbox.Group>
            <DefaultButtonAdd
              style={{ marginTop: "8px" }}
              text={"Add to Entry"}
              onClick={handleConfirmSelection}
            />
          </ScrollArea>
        </div>
      </Modal>

      <SavingModal opened={saving} onClose={() => setSaving(false)} />

      {/* print preview */}
      <FullScreenModal opened={preview} onClose={() => setPreview(false)}>
        {recordId && <AdminCreditJournalPreview id={recordId} />}
      </FullScreenModal>
    </Stack>
  );
};

export default AdminCreditJournalEntryPage;
